/**
 * フロー図と詳細ペインの並べ方（骨格）。
 *
 * **置き場所そのものを部品にする。** 詳細ペインの中身は既に共通だったが、
 * それをどこへ置くかは画面ごとに手で書いていた。その結果、マスターから見た図は右に、PJ から
 * 見た図は下に出るという食い違いが生まれ、同じ「ノードを選ぶと説明が出る」体験が画面によって
 * 別物になっていた。器をここへ寄せて、置き場所の判断を 1 箇所に閉じる。
 *
 * **左右比較のときだけ下へ回す。** 図を 2 枚並べる面では、右に 400px を取ると 1 枚あたりが
 * 業務 2 個分の幅しか持てず、並べて比べる用途に足りない。比較は「2 枚を見比べる」ことが
 * 主目的なので、その面に限って詳細を図の下へ逃がす。逆に言えば、それ以外はすべて右である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図と詳細ペインの並べ方を 1 箇所に閉じ、マスター・PJ の両画面で同じ骨格にする。
 * @graph-connects none
 */
import type { ReactNode } from 'react';

/**
 * 詳細ペインの幅。図の側は残りを取る。
 *
 * 固定幅にするのは、図の幅が内容で変わっても詳細の折り返し位置が動かないようにするため。
 * 読み手が同じ場所で同じ情報を探せることを、図の広さより優先する。
 * @graph-connects none
 */
const PANEL_CLASS = 'w-full shrink-0 lg:w-[400px]';

/** `FlowWithDetailLayout` の props。 */
export interface FlowWithDetailLayoutProps {
  /** フロー図（1 枚でも、比較の 2 枚でもよい）。 */
  diagram: ReactNode;
  /**
   * 選択したノードの詳細ペイン。省けば詳細の列を取らず、図が幅いっぱいを使う（業務フローの図は
   * 節点を押すまで右パネルを閉じる）。`null` を渡した画面は今までどおり列を取る。
   */
  detail?: ReactNode;
  /**
   * 詳細を図の下へ回すか。左右比較の面だけ `true` にする。
   *
   * 既定を「右」にしてあるのは、例外のほうを明示させるためである。既定が下だと、新しく足した
   * 面が黙って下に出て、また画面ごとに体験が割れる。
   */
  detailBelow?: boolean;
}

/**
 * フロー図と詳細ペインを並べる。
 *
 * @param props 図・詳細・並べ方
 * @returns 並べた骨格
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図と詳細ペインを既定で左右に、左右比較の面でだけ上下に並べ、詳細を省けば図だけを幅いっぱいに置く。
 * @graph-connects none
 */
export function FlowWithDetailLayout({
  diagram,
  detail,
  detailBelow = false,
}: FlowWithDetailLayoutProps) {
  if (detail === undefined) return <div className="min-w-0">{diagram}</div>;
  if (detailBelow) {
    return (
      <div className="flex flex-col gap-4">
        <div className="min-w-0">{diagram}</div>
        <div className="w-full">{detail}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1">{diagram}</div>
      <div className={PANEL_CLASS}>{detail}</div>
    </div>
  );
}
